import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { IAuth } from '../../interfaces/IAuth';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { to64decode, to64encode } from "../../helpers/base64";
import { apiOfflineAsync } from "../../layouts/pages/pages.component";
import { asyncForEach } from "../../helpers/asyncForEach";
import { firstValueFrom, map, filter } from 'rxjs';
import { getLanguageCookie } from 'src/assets/language';

interface AuthResponse {
  token: string;
  status?: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public httpClient: HttpClient
  private lastTokenRenewal: number = 0;
  private readonly RENEWAL_INTERVAL: number = 300000; // 5 minutos em milissegundos
  private static queueAlert: any = [];
  private static queueTime: any = [];

  private header: any = []
  private requestOptions: any = []

  public token: any = {};
  public me: any = {};
  public environment: any = {};
  public environmentsCount: any = 0;
  public corporation: any = {};
  public corporationsCount: any = 0;
  API_PATH: any = '';

  constructor(
    private HttpClient: HttpClient,
    private router: Router
  ) {
    this.httpClient = HttpClient;
    this.API_PATH = environment.apiUrl;

    this.header['Content-Type'] = 'application/json';
    this.requestOptions = { headers: new HttpHeaders(this.header) };
  }

  async setCookie({
    environment = null,
    corporation = null,
    me = null,
    modules = null,
    screens = null,
    profile = null,
    permissions = null,
    language = null,
    config = null,
    user_environment = null,
    user_corporation = null,
    users_environments = null,
    users_corporations = null,
  }: any = {}) {
    if (environment) {
      localStorage.removeItem('environment');
      localStorage.setItem('environment', to64encode(environment));
    }
    if (corporation) {
      localStorage.removeItem('corporation');
      localStorage.setItem('corporation', to64encode(corporation));
    }
    if (me) {
      localStorage.removeItem('me');
      localStorage.setItem('me', to64encode(me));
    }
    if (modules) {
      localStorage.removeItem('modules');
      localStorage.setItem('modules', to64encode(modules));
    }
    if (screens) {
      localStorage.removeItem('screens');
      localStorage.setItem('screens', to64encode(screens));
    }
    if (profile) {
      localStorage.removeItem('profile');
      localStorage.setItem('profile', to64encode(profile));
    }
    if (permissions) {
      localStorage.removeItem('permissions');
      localStorage.setItem('permissions', to64encode(permissions));
    }
    if (language) {
      localStorage.removeItem('language');
      localStorage.setItem('language', to64encode(language));
    }
    if (config) {
      localStorage.removeItem('config');
      localStorage.setItem('config', to64encode(config));
    }
    if (user_corporation) {
      localStorage.removeItem('user_corporation');
      localStorage.setItem('user_corporation', to64encode(user_corporation));
    }
    if (user_environment) {
      localStorage.removeItem('user_environment');
      localStorage.setItem('user_environment', to64encode(user_environment));
    }
    if (users_corporations) {
      localStorage.removeItem('users_corporations');
      localStorage.setItem('users_corporations', to64encode(users_corporations));
    }
    if (users_environments) {
      localStorage.removeItem('users_environments');
      localStorage.setItem('users_environments', to64encode(users_environments));
    }
  }

  setCookieLanguage() {
    if (!localStorage.getItem('language')) {
      localStorage.setItem('language', to64encode({ lang: 'en', file: 'us.svg' }));
    }
  }

  async redirectError(error: any) {
    if (error.status == 401) {
      return await this.navigate('/login');
    }
    return error.error;
  }

  async navigate(route: string) {
    return await this.router.navigate([route]);
  }

  async deleteCookie(values: any = []) {
    let array: any = document.cookie.split("; ")

    await asyncForEach(array, async (item: any) => {
      if (item == '') return;

      let split: any = item.split('=')

      if (values.includes(split[0])) {
        return
      }

      localStorage.removeItem(split[0])
    })
  }

  private getURLSearchParams({ environment, corporation }: any = {}) {
    let params = new URLSearchParams();

    if (!environment && localStorage.getItem('environment')) {
      environment = to64decode(localStorage.getItem('environment'))
    }

    if (!corporation && localStorage.getItem('corporation')) {
      corporation = to64decode(localStorage.getItem('corporation'))
    }

    if (environment && environment.slug) {
      params.set('environment', environment.slug);
    }

    if (corporation && corporation.slug) {
      params.set('corporation', corporation.slug);
    }

    return params.toString();
  }

  async setVersions(version_ui: any = null) {
    if (!version_ui) {
      return
    }
    return await this.httpClient.post<IAuth>(`${this.API_PATH}versions`, { version_ui }).toPromise()
  }

  async login({ email, password }: any = {}) {
    try {
      const response = await firstValueFrom(
        this.httpClient.post<AuthResponse>(`${this.API_PATH}auth/login`, { email, password }, { ...this.requestOptions, observe: 'response' }).pipe(
          filter((res): res is HttpResponse<AuthResponse> => res instanceof HttpResponse),
          map(res => res.body!)
        )
      );
      
      if (response && response.token) {
        localStorage.setItem('token', response.token);
      }
      return response;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        throw {
          status: error.status,
          message: error.error?.message || 'Erro ao fazer login'
        };
      }
      throw error;
    }
  }

  async register(data: any) {
    return await this.httpClient.post<any>(
      `${this.API_PATH}users/register`,
      data,
      this.requestOptions
    ).toPromise();
  }

  async getMe() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token não encontrado');
      }

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });

      const response = await firstValueFrom(
        this.httpClient.get<any>(`${this.API_PATH}me?${this.getURLSearchParams()}`, { headers })
      );
      return response;
    } catch (error) {
      console.error('Erro no getMe:', error);
      throw error;
    }
  }

  private addAlertLogin(type: string = '', text: string = '') {
    const someAlert = AuthService.queueAlert.find((alert: { text: string }) => alert.text === text);

    if (someAlert) {
      this.removeAlertLogin(someAlert.id)
      clearTimeout(AuthService.queueTime[someAlert.id]);
    }

    AuthService.queueAlert.push({ id: AuthService.queueAlert.length, type, text })
    AuthService.queueTime[AuthService.queueAlert.length] = setTimeout(() => {
      this.removeAlertLogin(AuthService.queueAlert.length)
    }, 3000)
  }

  private removeAlertLogin(id: number) {
    AuthService.queueAlert.splice(AuthService.queueAlert.findIndex((item: { id: number; }) => item.id == id), 1)
  }

  async authExpired() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token não encontrado');
      }

      // Decodifica o token atual
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      
      // Se faltar menos de 5 minutos para expirar, renova o token
      if (tokenData.exp - now < 300) { // 300 segundos = 5 minutos
        const response = await firstValueFrom(
          this.httpClient.get<AuthResponse>(`${this.API_PATH}auth/expired`, { observe: 'response' }).pipe(
            filter((res): res is HttpResponse<AuthResponse> => res instanceof HttpResponse),
            map(res => res.body!)
          )
        );
        
        if (response && response.token) {
          localStorage.setItem('token', response.token);
          this.lastTokenRenewal = Date.now();
        }
        return response;
      }

      return { token };
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.addAlertLogin('danger', this.getLanguages('error/invalid_token'));
        await this.navigate('/login');
      }
      throw error;
    }
  }
  
  getLanguages(file: string){
    return getLanguageCookie(file, localStorage.getItem('language'))
  }
  
  // Método para atualizar o token a cada interação do usuário
  async updateTokenOnInteraction() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const now = Date.now();
      // Verifica se já passou o intervalo mínimo desde a última renovação
      if (now - this.lastTokenRenewal < this.RENEWAL_INTERVAL) {
        return;
      }

      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(now / 1000);
      
      // Se faltar menos de 5 minutos para expirar, renova o token
      if (tokenData.exp - currentTime < 300) {
        await this.authExpired();
        this.lastTokenRenewal = now;
      }
    } catch (error) {
      console.error('Erro ao atualizar token:', error);
    }
  }

  async sendResetPassword(email: any) {
    try {
      return this.httpClient.get<any>(`${this.API_PATH}auth/reset/password/${email}`).toPromise()
    } catch (error: any) {
      await apiOfflineAsync(error)
    }
  }

  async setUserPassword(user_uuid: string, password: string) {
    return this.httpClient.put<any>(
      `${this.API_PATH}auth/reset/password/${user_uuid}`,
      { password },
      this.requestOptions
    ).toPromise()
  }
}
